import { useEffect, useRef } from 'react';
import { useGoogleMap } from '@react-google-maps/api';

/**
 * <AdvancedMarker> — drop-in replacement for the deprecated <Marker> component
 * (deprecated by Google in Feb 2024, replaced by google.maps.marker.AdvancedMarkerElement).
 *
 * Works only inside a <GoogleMap> that has a `mapId` prop set
 * (see GOOGLE_MAPS_MAP_ID in /app/frontend/src/lib/googleMaps.js).
 *
 * Props:
 *   position    {lat, lng}   — required
 *   title       string       — tooltip on hover
 *   onClick     fn           — click handler (receives the click event)
 *   zIndex      number
 *   gmpClickable bool        — defaults true when onClick is provided
 *
 *   // Visual content — pass ONE of:
 *   content     HTMLElement  — explicit DOM node
 *   contentHTML string       — HTML string (will be wrapped in a div)
 *   icon        { color, label, size?, scale? } — quick pin (color + 1-2 char label)
 *
 * Default visual: a 12px blue dot.
 */
export default function AdvancedMarker({
  position,
  title,
  onClick,
  zIndex,
  gmpClickable,
  content,
  contentHTML,
  icon,
  opacity,
}) {
  const map = useGoogleMap();
  const markerRef = useRef(null);
  const clickListenerRef = useRef(null);

  // Build the DOM content for the marker.
  const buildContent = () => {
    if (content instanceof HTMLElement) return content;
    if (typeof contentHTML === 'string' && contentHTML.length > 0) {
      const wrap = document.createElement('div');
      wrap.innerHTML = contentHTML;
      return wrap;
    }
    if (icon && icon.color) {
      const scale = icon.scale ?? 1;
      const size = icon.size ?? 28 * scale;
      const fontSize = Math.max(9, Math.round(size * 0.36));
      const wrap = document.createElement('div');
      wrap.style.cssText = `
        width:${size}px;height:${size}px;border-radius:50%;
        background:${icon.color};color:#fff;border:2px solid #fff;
        box-shadow:0 1px 3px rgba(0,0,0,.35);
        display:flex;align-items:center;justify-content:center;
        font-weight:700;font-size:${fontSize}px;font-family:system-ui,sans-serif;
        ${opacity != null ? `opacity:${opacity};` : ''}
      `.replace(/\s+/g, ' ');
      if (icon.label) wrap.textContent = icon.label;
      return wrap;
    }
    // Default: small blue dot
    const wrap = document.createElement('div');
    wrap.style.cssText = `
      width:12px;height:12px;border-radius:50%;
      background:#002FA7;border:2px solid #fff;
      box-shadow:0 1px 3px rgba(0,0,0,.35);
      ${opacity != null ? `opacity:${opacity};` : ''}
    `.replace(/\s+/g, ' ');
    return wrap;
  };

  // Create the marker once Map + library are ready.
  useEffect(() => {
    if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return undefined;
    const AdvancedMarkerElement = window.google.maps.marker.AdvancedMarkerElement;

    const marker = new AdvancedMarkerElement({
      map,
      position,
      title: title || undefined,
      content: buildContent(),
      zIndex,
      gmpClickable: gmpClickable ?? !!onClick,
    });
    markerRef.current = marker;

    if (onClick) {
      clickListenerRef.current = marker.addListener('click', onClick);
    }

    return () => {
      if (clickListenerRef.current) {
        clickListenerRef.current.remove();
        clickListenerRef.current = null;
      }
      marker.map = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line
  }, [map]);

  // Keep marker in sync with prop changes (position / title / content).
  useEffect(() => {
    if (!markerRef.current || !position) return;
    markerRef.current.position = position;
  }, [position?.lat, position?.lng]);

  useEffect(() => {
    if (!markerRef.current) return;
    markerRef.current.title = title || '';
  }, [title]);

  useEffect(() => {
    if (!markerRef.current) return;
    markerRef.current.content = buildContent();
    // eslint-disable-next-line
  }, [content, contentHTML, JSON.stringify(icon || {}), opacity]);

  useEffect(() => {
    if (!markerRef.current) return;
    if (zIndex !== undefined) markerRef.current.zIndex = zIndex;
  }, [zIndex]);

  // Re-bind click handler when it changes
  useEffect(() => {
    if (!markerRef.current) return undefined;
    if (clickListenerRef.current) {
      clickListenerRef.current.remove();
      clickListenerRef.current = null;
    }
    if (onClick) {
      markerRef.current.gmpClickable = true;
      clickListenerRef.current = markerRef.current.addListener('click', onClick);
    }
    return undefined;
  }, [onClick]);

  return null;
}
